import { Button } from '@base-ui/react/button';
import { Input } from '@base-ui/react/input';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';
import { DraftAncestorNotice } from '~/components/ui/DraftAncestorNotice';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { BackLink, ForwardLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import buttonStyles from '~/styles/button.module.css';
import { CertificateTemplateControl } from './CertificateTemplateControl';

type CourseAdmin = components['schemas']['CourseAdmin'];

function invalidateCourse(courseId: number, version: number) {
  queryClient.invalidateQueries({
    queryKey: api.adminGetCourse.getQueryKey({
      path: { course_id: courseId },
      query: { version },
    }),
  });
  queryClient.invalidateQueries({
    queryKey: api.adminListCourses.getQueryKey({}),
  });
}

export const Route = createFileRoute('/_admin/courses/$courseId/$version')({
  component: CourseDetail,
});

function CourseDetail() {
  const { courseId, version } = Route.useParams();
  const courseIdNum = Number(courseId);
  const versionNum = Number(version);
  const [editing, setEditing] = useState(false);

  // Admin-only read: the student course_detail is gated to a fully published
  // path, so a draft course would 404 there.
  const admin = api.adminGetCourse.useQuery({
    path: { course_id: courseIdNum },
    query: { version: versionNum },
  });

  if (admin.isLoading) return <Message>Loading course...</Message>;
  if (admin.error || !admin.data)
    return <Message $error>Error loading course</Message>;

  const course = admin.data;
  const certificateTitle = course.certificate_template_title;

  return (
    <Container>
      <BackLink>
        <Link to="/courses">&larr; Back to Courses</Link>
      </BackLink>

      <Header>
        <PageTitle>
          {course.title} <VersionTag>v{course.version}</VersionTag>
        </PageTitle>
        {!editing && (
          <Button
            className={buttonStyles.Button}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        )}
      </Header>

      {editing ? (
        <CourseEditor
          key={`${course.id}-${course.version}`}
          course={course}
          onDone={() => setEditing(false)}
        />
      ) : (
        <>
          <Section>
            <SectionTitle>Overview</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>ID</FieldLabel>
                <FieldValue>{course.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Version</FieldLabel>
                <FieldValue>{course.version}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Language</FieldLabel>
                <FieldValue>{course.language ?? '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Slug</FieldLabel>
                <FieldValue>{course.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Order</FieldLabel>
                <FieldValue>{course.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Status</FieldLabel>
                <FieldValue>{course.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Study Helper Required</FieldLabel>
                <FieldValue>
                  {course.study_helper_required ? 'Yes' : 'No'}
                </FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>Certificate</SectionTitle>
            <CertificateRow>
              <div>
                <FieldValue>
                  {certificateTitle !== null ? (
                    <>
                      Students who complete this course earn{' '}
                      <CertificateName>{certificateTitle}</CertificateName>.
                    </>
                  ) : (
                    <>No certificate. Completing this course earns nothing.</>
                  )}
                </FieldValue>
              </div>
              <CertificateTemplateControl
                owner={{ type: 'course', id: course.id }}
                hasTemplate={certificateTitle !== null}
                defaultTitle={course.title}
                onChanged={() => invalidateCourse(course.id, course.version)}
              />
            </CertificateRow>
          </Section>

          <ForwardLink>
            <Link
              to="/lessons"
              search={{ course: courseIdNum, version: versionNum }}
            >
              View lessons &rarr;
            </Link>
          </ForwardLink>
        </>
      )}
    </Container>
  );
}

function CourseEditor({
  course,
  onDone,
}: {
  course: CourseAdmin;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(course.title);
  const [slug, setSlug] = useState(course.slug);
  const [order, setOrder] = useState(String(course.order));
  const [status, setStatus] = useState(course.status);

  const invalidate = () => invalidateCourse(course.id, course.version);

  const updateMutation = api.adminUpdateCourse.useMutation(undefined, {
    onSuccess: () => {
      invalidate();
      onDone();
    },
  });

  const trimmedTitle = title.trim();
  // A published course is frozen structurally until content versioning exists:
  // order drives next-course progression and slug is a live student URL, so the
  // server rejects changing them (or unpublishing). Reflect that in the form.
  const isPublished = course.status === 'published';
  const willPublish = !isPublished && status === 'published';

  const handleSave = () => {
    if (!trimmedTitle || updateMutation.isPending) return;
    updateMutation.mutate({
      path: { course_id: course.id },
      query: { version: course.version },
      body: {
        title: trimmedTitle,
        slug,
        order: Number(order),
        status,
      },
    });
  };

  return (
    <Section>
      <SectionTitle>Edit course</SectionTitle>
      <FormGrid>
        <FormRow>
          <FormLabel htmlFor="course-title">Title</FormLabel>
          <TextInput
            id="course-title"
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="course-slug">Slug</FormLabel>
          <TextInput
            id="course-slug"
            value={slug}
            disabled={isPublished}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSlug(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="course-order">Order</FormLabel>
          <TextInput
            id="course-order"
            type="number"
            value={order}
            disabled={isPublished}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOrder(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="course-status">Status</FormLabel>
          {isPublished ? (
            <PublishedNote>
              Published and live for students. Order, slug and publication are
              frozen while it's live and can't be changed here.
            </PublishedNote>
          ) : (
            <>
              <StatusSelect
                id="course-status"
                value={status}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setStatus(e.target.value as CourseAdmin['status'])
                }
              >
                <option value="draft">draft</option>
                <option value="published">published</option>
              </StatusSelect>
              {willPublish && (
                <PublishWarning>
                  Publishing is permanent — a published course can't be returned
                  to draft or reordered, since that would disrupt students who
                  are partway through the series.
                </PublishWarning>
              )}
            </>
          )}
          <DraftAncestorNotice
            entityLabel="course"
            ancestors={[{ label: 'series', status: course.series_status }]}
          />
        </FormRow>
      </FormGrid>

      {updateMutation.isError && (
        <Message $error>Could not save changes. Please try again.</Message>
      )}

      <Actions>
        <Button
          className={buttonStyles.Button}
          onClick={handleSave}
          disabled={updateMutation.isPending || !trimmedTitle}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button className={buttonStyles.Button} onClick={onDone}>
          Cancel
        </Button>
      </Actions>
    </Section>
  );
}

const CertificateName = styled.strong`
  color: var(--grey-900);
  font-weight: 600;
`;

const CertificateRow = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
  padding: var(--inner-padding);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--component-margin);
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: var(--inner-padding);
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const FormLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--grey-700);
`;

const fieldStyles = `
  box-sizing: border-box;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--grey-300);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: var(--grey-50);
  color: var(--grey-900);

  &:focus {
    outline: 2px solid var(--blue);
    outline-offset: -1px;
  }
`;

const TextInput = styled(Input)`
  ${fieldStyles}
`;

const StatusSelect = styled.select`
  ${fieldStyles}
`;

const PublishWarning = styled.p`
  margin: 0.5rem 0 0;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid var(--orange);
  border-radius: 0.25rem;
  background: color-mix(in srgb, var(--orange) 12%, transparent);
  color: var(--grey-800);
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const PublishedNote = styled.p`
  margin: 0;
  color: var(--grey-700);
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const VersionTag = styled.small`
  color: var(--grey-600);
  font-weight: 400;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: var(--inner-padding);
`;

const Container = styled.div``;

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
